import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type {
  ReferrerAllowedStatus,
  ReferrerApplication,
  ReferrerStatistics,
  LeaderboardEntry,
  LeaderboardCurrentUser,
  PaginatedResponse
} from '@/types/referrerDashboard'

interface ApplicationsFilters {
  application_status?: string
  interview_status?: string
  referral_status?: string
  timeframe?: string
  submitted?: boolean
  search?: string
}

export const useMyReferralsStore = defineStore('myReferrals', () => {
  const applications = ref<ReferrerApplication[]>([])
  const statistics = ref<ReferrerStatistics | null>(null)
  const leaderboard = ref<LeaderboardEntry[]>([])
  const leaderboardCurrentUser = ref<LeaderboardCurrentUser | null>(null)
  const totalCount = ref(0)
  const currentPage = ref(1)
  const pageSize = ref(20)
  const nextPageUrl = ref<string | null>(null)
  const previousPageUrl = ref<string | null>(null)

  const isLoading = ref(false)
  const isStatsLoading = ref(false)
  const isLeaderboardLoading = ref(false)
  const error = ref<string | null>(null)

  const applicationStatusFilter = ref('')
  const interviewStatusFilter = ref('')
  const referralStatusFilter = ref('')
  const timeframeFilter = ref('')
  const submittedFilter = ref(false)
  const searchFilter = ref('')

  // eslint-disable-next-line complexity
  async function fetchMyApplications(filters: ApplicationsFilters = {}, page = 1) {
    isLoading.value = true
    error.value = null
    try {
      const queryParams = new URLSearchParams()
      queryParams.append('page', String(page))
      if (filters.application_status) {
        queryParams.append('application_status', filters.application_status)
      }
      if (filters.interview_status) {
        queryParams.append('interview_status', filters.interview_status)
      }
      if (filters.referral_status) {
        queryParams.append('referral_status', filters.referral_status)
      }
      if (filters.timeframe) {
        queryParams.append('timeframe', filters.timeframe)
      }
      if (filters.submitted) {
        queryParams.append('submitted', 'true')
      }
      if (filters.search) {
        queryParams.append('search', filters.search)
      }

      const url = queryParams.toString()
        ? `${API_ROUTES.REFERRER_MY_APPLICATIONS}?${queryParams.toString()}`
        : API_ROUTES.REFERRER_MY_APPLICATIONS
      const response = await api.get<PaginatedResponse<ReferrerApplication> | ReferrerApplication[]>(url)
      const payload = response.data as PaginatedResponse<ReferrerApplication> | ReferrerApplication[]

      // Backward-compatible parsing: supports both paginated and plain-array responses.
      if (Array.isArray(payload)) {
        applications.value = payload
        totalCount.value = payload.length
        nextPageUrl.value = null
        previousPageUrl.value = null
      } else {
        const results = Array.isArray(payload.results) ? payload.results : []
        applications.value = results
        totalCount.value = typeof payload.count === 'number' ? payload.count : results.length
        nextPageUrl.value = payload.next ?? null
        previousPageUrl.value = payload.previous ?? null
      }

      currentPage.value = page
      if (applications.value.length > 0 && page === 1) {
        pageSize.value = applications.value.length
      }
      return payload
    } catch (err) {
      error.value = 'Failed to fetch your referrals'
      console.error('Error fetching my referrals:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function fetchMyStatistics() {
    isStatsLoading.value = true
    error.value = null
    try {
      const response = await api.get(API_ROUTES.REFERRER_MY_STATISTICS)
      statistics.value = response.data
      return response.data
    } catch (err) {
      error.value = 'Failed to fetch referral statistics'
      console.error('Error fetching referral statistics:', err)
      throw err
    } finally {
      isStatsLoading.value = false
    }
  }

  async function fetchLeaderboard(limit = 20) {
    isLeaderboardLoading.value = true
    error.value = null
    try {
      const response = await api.get(API_ROUTES.REFERRER_LEADERBOARD, {
        params: { limit }
      })
      leaderboard.value = response.data.leaderboard ?? []
      leaderboardCurrentUser.value = response.data.current_user ?? null
      return response.data
    } catch (err) {
      error.value = 'Failed to fetch referrer leaderboard'
      console.error('Error fetching referrer leaderboard:', err)
      throw err
    } finally {
      isLeaderboardLoading.value = false
    }
  }

  async function updateReferralStatus(applicationId: number | string, status: ReferrerAllowedStatus) {
    error.value = null
    try {
      const response = await api.patch(API_ROUTES.REFERRER_UPDATE_REFERRAL_STATUS(applicationId), {
        referral_status: status
      })
      const index = applications.value.findIndex((app) => app.id === Number(applicationId))
      if (index !== -1) {
        applications.value[index] = response.data
      }
      return response.data
    } catch (err) {
      error.value = 'Failed to update referral status'
      console.error('Error updating referral status:', err)
      throw err
    }
  }

  function applyFilters(page = 1) {
    return fetchMyApplications({
      application_status: applicationStatusFilter.value || undefined,
      interview_status: interviewStatusFilter.value || undefined,
      referral_status: referralStatusFilter.value || undefined,
      timeframe: timeframeFilter.value || undefined,
      submitted: submittedFilter.value || undefined,
      search: searchFilter.value || undefined
    }, page)
  }

  function clearFilters() {
    applicationStatusFilter.value = ''
    interviewStatusFilter.value = ''
    referralStatusFilter.value = ''
    timeframeFilter.value = ''
    submittedFilter.value = false
    searchFilter.value = ''
    currentPage.value = 1
    nextPageUrl.value = null
    previousPageUrl.value = null
  }

  return {
    applications,
    statistics,
    leaderboard,
    leaderboardCurrentUser,
    totalCount,
    currentPage,
    pageSize,
    nextPageUrl,
    previousPageUrl,
    isLoading,
    isStatsLoading,
    isLeaderboardLoading,
    error,
    applicationStatusFilter,
    interviewStatusFilter,
    referralStatusFilter,
    timeframeFilter,
    submittedFilter,
    searchFilter,
    fetchMyApplications,
    fetchMyStatistics,
    fetchLeaderboard,
    updateReferralStatus,
    applyFilters,
    clearFilters
  }
})

